import React, { useState } from 'react';
import api from '../utils/api';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';

// Quick Create Product Modal
export const QuickCreateProduct = ({ onCreated, onClose }) => {
  const [formData, setFormData] = useState({ sku: '', name: '', product_category: 'rm', unit: 'pcs', quantity: 0, unit_price: 0 });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/sales/inventory/quick-create', formData);
      onCreated(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create product'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" data-testid="quick-create-product-modal">
      <div className="modal-content" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header flex items-center justify-between">
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Quick Create Product</h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="grid grid-cols-2">
              <div className="form-group"><label className="form-label">SKU *</label><input type="text" className="form-input" value={formData.sku} onChange={(e) => setFormData({...formData, sku: e.target.value})} required data-testid="qc-product-sku" /></div>
              <div className="form-group"><label className="form-label">Name *</label><input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="qc-product-name" /></div>
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-select" value={formData.product_category} onChange={(e) => setFormData({...formData, product_category: e.target.value})} data-testid="qc-product-category">
                  <option value="rm">Raw Material</option><option value="wip">WIP</option><option value="fg">Finished Good</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Unit</label><input type="text" className="form-input" value={formData.unit} onChange={(e) => setFormData({...formData, unit: e.target.value})} data-testid="qc-product-unit" /></div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="qc-product-submit">{submitting ? 'Creating...' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Quick Create Vendor Modal
export const QuickCreateVendor = ({ onCreated, onClose }) => {
  const [formData, setFormData] = useState({ name: '', gstin: '', contact_person: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/purchase/vendors/quick-create', formData);
      onCreated(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create vendor'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" data-testid="quick-create-vendor-modal">
      <div className="modal-content" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header flex items-center justify-between">
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Quick Create Vendor</h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Vendor Name *</label><input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="qc-vendor-name" /></div>
            <div className="grid grid-cols-2">
              <div className="form-group"><label className="form-label">GSTIN</label><input type="text" className="form-input" value={formData.gstin} onChange={(e) => setFormData({...formData, gstin: e.target.value})} data-testid="qc-vendor-gstin" /></div>
              <div className="form-group"><label className="form-label">Contact Person</label><input type="text" className="form-input" value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group"><label className="form-label">Phone</label><input type="text" className="form-input" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /></div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="qc-vendor-submit">{submitting ? 'Creating...' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Quick Create Item Group Modal
export const QuickCreateItemGroup = ({ onCreated, onClose }) => {
  const [name, setName] = useState('');
  const handleSubmit = async (e) => {
    e.preventDefault();
    try { const res = await api.post('/sales/item-groups', { name }); onCreated(res.data); onClose(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="modal-overlay" data-testid="quick-create-group-modal">
      <div className="modal-content" style={{ maxWidth: '360px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>New Item Group</h2></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Group Name *</label><input type="text" className="form-input" value={name} onChange={(e) => setName(e.target.value)} required data-testid="qc-group-name" /></div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button type="submit" className="btn btn-primary" data-testid="qc-group-submit">Create</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Quick Create Customer Modal
export const QuickCreateCustomer = ({ onCreated, onClose }) => {
  const [formData, setFormData] = useState({ name: '', gstin: '', contact_person: '', phone: '', email: '', address: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/purchase/customers/quick-create', formData);
      onCreated(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create customer'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }} data-testid="quick-create-customer-modal">
      <div className="modal-content" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header flex items-center justify-between">
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Quick Create Customer</h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Customer Name *</label><input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="qc-customer-name" /></div>
            <div className="grid grid-cols-2">
              <div className="form-group"><label className="form-label">GSTIN</label><input type="text" className="form-input" value={formData.gstin} onChange={(e) => setFormData({...formData, gstin: e.target.value})} data-testid="qc-customer-gstin" /></div>
              <div className="form-group"><label className="form-label">Contact Person</label><input type="text" className="form-input" value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group"><label className="form-label">Phone</label><input type="text" className="form-input" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /></div>
            </div>
            <div className="form-group"><label className="form-label">Address</label><textarea className="form-input" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} rows={2} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="qc-customer-submit">{submitting ? 'Creating...' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

